import { useMemo } from 'react';
import { IntlFormatters } from 'react-intl';
import { ItemType } from '../../constants/ItemType';
import { NAME_SPACE_SITE_FILES_TRANSLATIONS } from '../../constants/NameSpace';
import { ExternalSource, MainSource, Source } from '../../constants/Source';
import { SystemFolderId } from '../../constants/SystemFolderId';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { TMMGRSettings } from '../hoc/withMMGRSettings/withMMGRSettings.types';
import { safeFormatMessage } from '../safeFormatMessage';
import { MediaManagerSDKFolder } from './index';

export const getSourceNameTranslationId = (source: Source, { isSecureContext, namespace }: TMMGRSettings): string => {
  switch (source) {
    case MainSource.SiteFiles:
      return isSecureContext ? NAME_SPACE_SITE_FILES_TRANSLATIONS[namespace] : 'categories.site-files';

    case MainSource.VisitorsUploads:
      return 'categories.visitor-uploads';

    case MainSource.Favorites:
      return 'categories.favorites';

    case MainSource.Recommended:
      return 'categories.recommended';

    case MainSource.MediaFromWix:
      return 'categories.free-from-wix';

    case MainSource.NTT:
      return 'categories.ntt';

    case MainSource.Yell:
      return 'categories.yell';

    case MainSource.Enterprise:
      return 'categories.enterprise';

    case MainSource.Shutterstock:
      return 'categories.shutterstock';

    case MainSource.Unsplash:
      return 'categories.unsplash';

    case MainSource.Trash:
      return 'categories.trash';

    case MainSource.Boards:
      return 'categories.boards';

    case ExternalSource.Computer:
      return 'files-source.label.computer';

    case ExternalSource.Facebook:
      return 'files-source.label.facebook';

    case ExternalSource.Dribbble:
      return 'files-source.label.dribbble';

    case ExternalSource.GoogleDrive:
      return 'files-source.label.google-drive';

    case ExternalSource.Dropbox:
      return 'files-source.label.dropbox';

    case ExternalSource.Vimeo:
      return 'files-source.label.vimeo';

    case ExternalSource.Instagram:
      return 'files-source.label.instagram';

    case ExternalSource.Flickr:
      return 'files-source.label.flickr';

    case ExternalSource.DeviantArt:
      return 'files-source.label.deviant-art';

    case ExternalSource.WixSite:
      return 'files-source.label.wix-site';

    case ExternalSource.Wixel:
      return 'files-source.label.wixel';

    case ExternalSource.GooglePhotos:
      return 'files-source.label.google-photos';

    case ExternalSource.Link:
      return 'files-source.label.link';

    default:
      return null;
  }
};

export const useSourceNameTranslationId = (source: Source) => {
  const { mmgrSettings } = useMMGRSettings();
  return useMemo(() => getSourceNameTranslationId(source, mmgrSettings), [source, mmgrSettings]);
};

export interface IOuter {
  source: Source;
}

export interface IInner {
  rootFolder: MediaManagerSDKFolder;
}

export const getRootFolderIdForSource = (source: Source, mmgrSettings?: TMMGRSettings): string => {
  switch (source) {
    case MainSource.SiteFiles:
      return SystemFolderId.SiteFiles;

    case MainSource.VisitorsUploads:
      return SystemFolderId.VisitorUploads;

    case MainSource.Trash:
      return SystemFolderId.Trash;

    case ExternalSource.WixSite:
    case ExternalSource.Wixel:
      return SystemFolderId.WixSites;

    case MainSource.MediaFromWix:
    case MainSource.Enterprise:
      return '';

    case MainSource.Boards:
      return SystemFolderId.Boards;

    default:
      return null;
  }
};

/**
 * @deprecated use "useRootFolder"
 */
export const getRootFolderBySource = (
  source: Source,
  formatMessage: IntlFormatters['formatMessage'],
  mmgrSettings: TMMGRSettings,
): MediaManagerSDKFolder => ({
  id: getRootFolderIdForSource(source, mmgrSettings),
  name: safeFormatMessage(formatMessage, { id: getSourceNameTranslationId(source, mmgrSettings), defaultMessage: '/' }),
  kind: ItemType.Folder,
  source,
  siteId: null,
});
